<template>
  <div class="barcodes-page py-3">
    <header class="bc-toolbar">
      <div class="bc-toolbar__title">
        <span class="bc-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Barcode Inventory</h1>
          <p>Pre-printed courier article numbers — what we hold, and what has already gone out</p>
        </div>
      </div>
      <div class="bc-toolbar__actions">
        <CButton
          size="sm"
          class="bc-sync-btn"
          :class="{ 'is-loading': isSyncing }"
          :disabled="isSyncing || isLoading"
          title="Re-check every barcode against order tracking IDs"
          @click="syncUsage"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bc-sync-btn__icon" aria-hidden="true">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          <span>{{ isSyncing ? 'Syncing…' : 'Sync Usage' }}</span>
        </CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="reloadActiveTab"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="bc-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="bc-alert" @click.native="successMessage = ''">{{ successMessage }}</CAlert>

    <div class="bc-tab-bar" role="tablist">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        type="button"
        role="tab"
        :class="['bc-tab', { 'is-active': activeTab === tab.key }]"
        :aria-selected="activeTab === tab.key"
        @click="switchTab(tab.key)"
      >
        <span class="bc-tab__label">{{ tab.label }}</span>
      </button>
    </div>

    <!-- ───────────────────────── Overview ───────────────────────── -->
    <template v-if="activeTab === 'overview'">
      <div class="bc-kpis">
        <article v-for="kpi in kpiCards" :key="kpi.key" class="bc-kpi">
          <span :class="['bc-kpi__chip', `bc-kpi__chip--${kpi.tone}`]" aria-hidden="true" v-html="kpi.icon"></span>
          <div class="bc-kpi__body">
            <p class="bc-kpi__label">{{ kpi.label }}</p>
            <p :class="['bc-kpi__value', { 'is-brand': kpi.key === 'total' }]">{{ kpi.value }}</p>
            <p class="bc-kpi__hint">{{ kpi.hint }}</p>
          </div>
        </article>
      </div>

      <div class="bc-grid">
        <section class="bc-section bc-section--meter">
          <header class="bc-section__head">
            <h2>Stock Utilisation</h2>
            <small class="bc-section__hint">{{ formatNumber(totals.used) }} of {{ formatNumber(totals.total) }} consumed</small>
          </header>
          <div class="bc-section__body">
            <p class="bc-meter-figure">
              <span class="bc-meter-figure__value">{{ totals.utilisation }}%</span>
              <span class="bc-meter-figure__unit">used</span>
            </p>

            <div
              class="bc-meter"
              role="img"
              :aria-label="`${totals.used} used, ${totals.available} available, ${totals.void} voided`"
            >
              <span
                v-for="seg in utilisationSegments"
                :key="seg.key"
                :class="['bc-meter__seg', `bc-meter__seg--${seg.key}`]"
                :style="{ width: seg.width }"
                :title="`${seg.label}: ${formatNumber(seg.value)}`"
              ></span>
              <span v-if="!totals.total" class="bc-meter__empty"></span>
            </div>

            <ul class="bc-legend">
              <li v-for="seg in utilisationSegments" :key="seg.key" class="bc-legend__item">
                <span :class="['bc-legend__swatch', `bc-legend__swatch--${seg.key}`]" aria-hidden="true"></span>
                <span class="bc-legend__label">{{ seg.label }}</span>
                <span class="bc-legend__value">{{ formatNumber(seg.value) }}</span>
              </li>
            </ul>

            <p class="bc-meter-note">
              A barcode counts as <strong>used</strong> the moment it appears as an order's tracking ID.
              Last checked {{ lastSyncLabel }}.
            </p>
          </div>
        </section>

        <section class="bc-section bc-section--chart">
          <header class="bc-section__head">
            <h2>Labels Consumed per Month</h2>
            <small class="bc-section__hint">Last 12 months</small>
          </header>
          <div class="bc-section__body">
            <div v-if="!hasTrendData" class="bc-empty">No dispatches recorded yet for this scope.</div>
            <div v-else class="bc-chart" @mouseleave="hoveredBar = -1">
              <svg :viewBox="`0 0 ${chart.width} ${chart.height}`" class="bc-chart__svg" role="img" aria-label="Barcodes consumed per month">
                <g>
                  <line
                    v-for="tick in chartTicks"
                    :key="`grid-${tick.value}`"
                    class="bc-chart__grid"
                    :x1="chart.padLeft"
                    :x2="chart.width - chart.padRight"
                    :y1="tick.y"
                    :y2="tick.y"
                  />
                  <text
                    v-for="tick in chartTicks"
                    :key="`lbl-${tick.value}`"
                    class="bc-chart__axis"
                    :x="chart.padLeft - 8"
                    :y="tick.y + 4"
                    text-anchor="end"
                  >{{ tick.label }}</text>
                </g>

                <g v-for="bar in trendBars" :key="bar.month">
                  <rect
                    class="bc-chart__hit"
                    :x="bar.bandX"
                    :y="chart.padTop"
                    :width="bar.bandW"
                    :height="chart.plotH"
                    @mouseenter="hoveredBar = bar.index"
                  />
                  <path
                    :class="['bc-chart__bar', { 'is-hovered': hoveredBar === bar.index }]"
                    :d="bar.path"
                  />
                  <text
                    class="bc-chart__axis"
                    :x="bar.bandX + bar.bandW / 2"
                    :y="chart.height - 6"
                    text-anchor="middle"
                  >{{ bar.label }}</text>
                </g>
              </svg>

              <div
                v-if="hoveredTrend"
                class="bc-chart__tip"
                :style="{ left: hoveredTrend.tipLeft }"
              >
                <span class="bc-chart__tip-head">{{ hoveredTrend.label }} {{ hoveredTrend.year }}</span>
                <span class="bc-chart__tip-value">{{ formatNumber(hoveredTrend.used) }} labels</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="bc-grid">
        <section class="bc-section">
          <header class="bc-section__head">
            <h2>By Series</h2>
            <small class="bc-section__hint">Article-number prefix</small>
          </header>
          <div class="bc-section__body">
            <div v-if="!series.length" class="bc-empty">No barcodes stored yet.</div>
            <ul v-else class="bc-series">
              <li v-for="row in series" :key="row.prefix" class="bc-series__row">
                <div class="bc-series__head">
                  <button type="button" class="bc-series__name" @click="drillIntoSeries(row.prefix)">{{ row.prefix }}</button>
                  <span class="bc-series__counts">
                    <strong>{{ formatNumber(row.used) }}</strong> used · {{ formatNumber(row.available) }} free
                  </span>
                </div>
                <div class="bc-series__track">
                  <span class="bc-series__fill" :style="{ width: percentOf(row.used, row.total) }"></span>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section class="bc-section">
          <header class="bc-section__head">
            <h2>Recent Uploads</h2>
            <button type="button" class="bc-link-btn" @click="switchTab('batches')">View all batches</button>
          </header>
          <div class="bc-section__body">
            <div v-if="!recentBatches.length" class="bc-empty">Nothing uploaded yet. Head to the Upload tab to add a sheet.</div>
            <ul v-else class="bc-recent">
              <li v-for="batch in recentBatches" :key="batch.id" class="bc-recent__row">
                <div class="bc-recent__main">
                  <button type="button" class="bc-recent__name" @click="drillIntoBatch(batch)">
                    {{ batch.label || batch.source_filename }}
                  </button>
                  <span class="bc-recent__range">{{ batch.first_code }} → {{ batch.last_code }}</span>
                </div>
                <div class="bc-recent__meta">
                  <span class="bc-chip">{{ formatNumber(batch.total_count) }} codes</span>
                  <span class="bc-chip bc-chip--used">{{ batch.utilisation }}% used</span>
                  <span class="bc-recent__date">{{ formatDate(batch.created_at) }}</span>
                </div>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </template>

    <!-- ─────────────────────── All Barcodes ─────────────────────── -->
    <template v-else-if="activeTab === 'barcodes'">
      <section class="bc-section">
        <header class="bc-section__head">
          <h2>Filters</h2>
          <small class="bc-section__hint">{{ barcodeTotal ? `${formatNumber(barcodeTotal)} matching` : '' }}</small>
        </header>
        <div class="bc-section__body">
          <div class="bc-filters">
            <div class="bc-filter bc-filter--wide">
              <label class="bc-label" for="bc-search">Search barcode or order</label>
              <input
                id="bc-search"
                v-model.trim="filters.q"
                type="text"
                class="bc-input"
                placeholder="Scan or type e.g. EY480112477IN"
                @keydown.enter="applyBarcodeFilters"
              >
            </div>
            <div class="bc-filter">
              <label class="bc-label">Status</label>
              <AppDropdown v-model="filters.status" :options="statusFilterOptions" />
            </div>
            <div class="bc-filter">
              <label class="bc-label">Series</label>
              <AppDropdown v-model="filters.prefix" :options="prefixFilterOptions" />
            </div>
            <div class="bc-filter">
              <label class="bc-label">Batch</label>
              <AppDropdown
                v-model="filters.batch_id"
                :options="batchFilterOptions"
                searchable
                search-placeholder="Find a batch…"
              />
            </div>
            <div class="bc-filter bc-filter--range">
              <label class="bc-label">Used between</label>
              <DateRangePicker v-model="usedDateRange" variant="form" :presets="false" />
            </div>
            <div class="bc-filter bc-filter--action">
              <label class="bc-label" aria-hidden="true">&nbsp;</label>
              <div class="bc-filter-actions">
                <button type="button" class="bc-btn bc-btn--primary" :disabled="isLoading" @click="applyBarcodeFilters">
                  {{ isLoading ? '…' : 'Apply' }}
                </button>
                <button type="button" class="bc-btn bc-btn--ghost" :disabled="isLoading" @click="resetBarcodeFilters">Reset</button>
                <button type="button" class="bc-btn bc-btn--ghost" :disabled="isLoading || !barcodeTotal" @click="exportBarcodes">Export CSV</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BaseTable
        title="Barcodes"
        :items="barcodeRows"
        :columns="barcodeColumns"
        :loading="isLoading"
        :sortable="false"
        :show-count="false"
        :sticky-columns="1"
        max-body-height="65vh"
        empty-text="No barcodes match these filters."
      >
        <template #toolbar>
          <span class="bc-table-hint">Showing {{ barcodeRangeLabel }}</span>
        </template>

        <template #cell-code="{ row }">
          <span class="bc-code">{{ row.code }}</span>
        </template>

        <template #cell-status="{ row }">
          <span :class="['bc-pill', row.status_class]">{{ row.status_label }}</span>
        </template>

        <template #cell-order="{ row }">
          <span v-if="row.order" class="bc-order">{{ row.order }}</span>
          <span v-else class="bc-muted">—</span>
        </template>

        <template #cell-actions="{ row }">
          <div class="bc-row-actions">
            <button
              v-if="canManage && row.status === 'available'"
              type="button"
              class="bc-action-btn bc-action-btn--void"
              @click="askVoid(row)"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" /><line x1="6" y1="18" x2="18" y2="6" /></svg>
              <span>Void</span>
            </button>
            <button
              v-else-if="canManage && row.status === 'void'"
              type="button"
              class="bc-action-btn bc-action-btn--restore"
              @click="restoreBarcode(row)"
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></svg>
              <span>Restore</span>
            </button>
            <span v-else class="bc-muted">—</span>
          </div>
        </template>
      </BaseTable>

      <div v-if="!isLoading && barcodeRows.length && barcodeLastPage > 1" class="bc-pagination-bar">
        <span class="bc-pagination-info">{{ barcodeRangeLabel }}</span>
        <AppPager :current-page="barcodePage" :last-page="barcodeLastPage" @change="goToBarcodePage" />
      </div>
    </template>

    <!-- ───────────────────────── Batches ────────────────────────── -->
    <template v-else-if="activeTab === 'batches'">
      <section class="bc-section">
        <header class="bc-section__head">
          <h2>Uploaded Sheets</h2>
          <small class="bc-section__hint">{{ batchTotal ? `${formatNumber(batchTotal)} batches` : '' }}</small>
        </header>
        <div class="bc-section__body">
          <div class="bc-filters">
            <div class="bc-filter bc-filter--wide">
              <label class="bc-label" for="bc-batch-search">Search by name, file or code range</label>
              <input
                id="bc-batch-search"
                v-model.trim="batchSearch"
                type="text"
                class="bc-input"
                placeholder="e.g. ARTICLE_EY480112…"
                @keydown.enter="loadBatches(1)"
              >
            </div>
            <div class="bc-filter bc-filter--action">
              <label class="bc-label" aria-hidden="true">&nbsp;</label>
              <div class="bc-filter-actions">
                <button type="button" class="bc-btn bc-btn--primary" :disabled="isLoading" @click="loadBatches(1)">Search</button>
                <button type="button" class="bc-btn bc-btn--ghost" :disabled="isLoading" @click="batchSearch = ''; loadBatches(1)">Reset</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BaseTable
        title="Batches"
        :items="batchRows"
        :columns="batchColumns"
        :loading="isLoading"
        :sortable="false"
        :show-count="false"
        empty-text="No batches uploaded yet."
      >
        <template #toolbar>
          <span class="bc-table-hint">Showing {{ batchRangeLabel }}</span>
        </template>

        <template #cell-name="{ row }">
          <button type="button" class="bc-linkish" @click="drillIntoBatch(row.raw)">{{ row.name }}</button>
          <span class="bc-subtle">{{ row.source_filename }}</span>
        </template>

        <template #cell-range="{ row }">
          <span class="bc-code bc-code--sm">{{ row.range }}</span>
        </template>

        <template #cell-utilisation="{ row }">
          <div class="bc-mini-meter" :title="`${row.used_count} of ${row.total_count} used`">
            <span class="bc-mini-meter__fill" :style="{ width: row.utilisation + '%' }"></span>
          </div>
          <span class="bc-mini-meter__label">{{ row.utilisation }}%</span>
        </template>

        <template #cell-actions="{ row }">
          <div class="bc-row-actions">
            <button type="button" class="bc-action-btn bc-action-btn--view" @click="drillIntoBatch(row.raw)">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>
              <span>Codes</span>
            </button>
            <button v-if="row.has_file" type="button" class="bc-action-btn bc-action-btn--view" @click="downloadBatchPdf(row.raw)">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              <span>PDF</span>
            </button>
            <button v-if="canManage" type="button" class="bc-action-btn bc-action-btn--delete" @click="askDeleteBatch(row.raw)">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
              <span>Delete</span>
            </button>
          </div>
        </template>
      </BaseTable>

      <div v-if="!isLoading && batchRows.length && batchLastPage > 1" class="bc-pagination-bar">
        <span class="bc-pagination-info">{{ batchRangeLabel }}</span>
        <AppPager :current-page="batchPage" :last-page="batchLastPage" @change="goToBatchPage" />
      </div>
    </template>

    <!-- ────────────────────────── Upload ────────────────────────── -->
    <template v-else-if="activeTab === 'upload'">
      <section class="bc-section">
        <header class="bc-section__head">
          <h2>Upload Label Sheets</h2>
          <small class="bc-section__hint">PDF only · up to 20 files per upload</small>
        </header>
        <div class="bc-section__body">
          <div
            :class="['bc-drop', { 'is-active': isDragging }]"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="onDrop"
          >
            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="bc-drop__icon" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p class="bc-drop__title">Drop the India Post article-number PDFs here</p>
            <p class="bc-drop__hint">
              We read the printed numbers straight out of the PDF's text layer — no scanning needed.
              Codes already in the inventory are skipped automatically.
            </p>
            <button type="button" class="bc-btn bc-btn--primary" @click="$refs.fileInput.click()">Choose files</button>
            <input ref="fileInput" type="file" accept="application/pdf" multiple class="bc-drop__input" @change="onFilePick">
          </div>

          <ul v-if="pendingFiles.length" class="bc-filelist">
            <li v-for="(file, index) in pendingFiles" :key="`${file.name}-${index}`" class="bc-filelist__row">
              <span class="bc-filelist__name">{{ file.name }}</span>
              <span class="bc-filelist__size">{{ formatBytes(file.size) }}</span>
              <button type="button" class="bc-filelist__remove" :disabled="isUploading" @click="removeFile(index)" aria-label="Remove file">×</button>
            </li>
          </ul>

          <div class="bc-filters bc-filters--upload">
            <div class="bc-filter">
              <label class="bc-label">Courier</label>
              <AppDropdown v-model="uploadForm.courier" :options="courierOptions" />
            </div>
            <div class="bc-filter bc-filter--wide">
              <AppInput
                no-margin
                label="Batch name (optional)"
                :value="uploadForm.label"
                placeholder="e.g. Post office booking — 13 Aug"
                @update:value="uploadForm.label = $event"
              />
            </div>
            <div class="bc-filter bc-filter--full">
              <AppInput
                no-margin
                multiline
                :rows="2"
                label="Notes (optional)"
                :value="uploadForm.notes"
                placeholder="Anything worth remembering about this sheet"
                @update:value="uploadForm.notes = $event"
              />
            </div>
          </div>

          <div class="bc-upload-actions">
            <button
              type="button"
              class="bc-btn bc-btn--primary bc-btn--lg"
              :disabled="isUploading || !pendingFiles.length"
              @click="submitUpload"
            >
              {{ uploadButtonLabel }}
            </button>
            <button type="button" class="bc-btn bc-btn--ghost" :disabled="isUploading || !pendingFiles.length" @click="pendingFiles = []">Clear</button>
          </div>
        </div>
      </section>

      <section v-if="uploadResults.length" class="bc-section">
        <header class="bc-section__head">
          <h2>Last Upload</h2>
        </header>
        <div class="bc-section__body">
          <ul class="bc-results">
            <li v-for="(result, index) in uploadResults" :key="`${result.file}-${index}`" :class="['bc-result', result.ok ? 'is-ok' : 'is-bad']">
              <span class="bc-result__file">{{ result.file }}</span>
              <span v-if="result.ok" class="bc-result__detail">
                {{ formatNumber(result.inserted) }} stored
                <template v-if="result.duplicates"> · {{ formatNumber(result.duplicates) }} already known</template>
                <template v-if="result.first_code"> · {{ result.first_code }} → {{ result.last_code }}</template>
              </span>
              <span v-else class="bc-result__detail">{{ result.message }}</span>
            </li>
          </ul>
        </div>
      </section>
    </template>

    <AppConfirmDialog
      :visible="showVoidConfirm"
      title="Void this barcode?"
      :message="voidConfirmMessage"
      confirm-label="Void it"
      cancel-label="Keep"
      busy-label="Voiding…"
      :busy="isMutating"
      variant="danger"
      :close-on-backdrop="!isMutating"
      @confirm="confirmVoid"
      @cancel="showVoidConfirm = false"
    />

    <AppConfirmDialog
      :visible="showDeleteConfirm"
      title="Delete this batch?"
      :message="deleteConfirmMessage"
      confirm-label="Delete"
      cancel-label="Keep"
      busy-label="Deleting…"
      :busy="isMutating"
      variant="danger"
      :close-on-backdrop="!isMutating"
      @confirm="confirmDeleteBatch"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import AppPager from '@/components/AppPager.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import BaseTable from '@/components/BaseTable.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import { getStoredRoles } from '@/auth/access'

// Mirrors BarcodeController::MANAGE_ROLES. Keep the two lists in step —
// the server is the real gate, this only hides controls that would 403.
const MANAGE_ROLES = [
  'superadmin', 'admin', 'finance_manager', 'senior_accountant_2',
  'moderator', 'sales_admin', 'storekeeper', 'operations'
]

// Chart geometry. A fixed viewBox scaled by CSS keeps the bar maths simple
// and the whole thing responsive without a resize observer.
const CHART = {
  width: 720,
  height: 210,
  padLeft: 40,
  padRight: 10,
  padTop: 10,
  padBottom: 26
}
CHART.plotW = CHART.width - CHART.padLeft - CHART.padRight
CHART.plotH = CHART.height - CHART.padTop - CHART.padBottom

const ICONS = {
  total: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="3" y1="4" x2="3" y2="20"/><line x1="7" y1="4" x2="7" y2="20"/><line x1="11" y1="4" x2="11" y2="20"/><line x1="16" y1="4" x2="16" y2="20"/><line x1="21" y1="4" x2="21" y2="20"/></svg>',
  used: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>',
  available: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/></svg>',
  void: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><line x1="6" y1="18" x2="18" y2="6"/></svg>',
  batches: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v5H4z"/><path d="M4 13h16v7H4z"/></svg>'
}

function blankTotals () {
  return { total: 0, used: 0, available: 0, void: 0, batches: 0, utilisation: 0, used_this_month: 0, added_this_month: 0 }
}

export default {
  name: 'Barcodes',
  components: { AppDropdown, AppInput, AppPager, AppConfirmDialog, BaseTable, DateRangePicker },
  data () {
    return {
      activeTab: 'overview',
      isLoading: false,
      isSyncing: false,
      isUploading: false,
      isMutating: false,
      errorMessage: '',
      successMessage: '',
      lastSyncedAt: null,

      // Overview
      totals: blankTotals(),
      series: [],
      trend: [],
      recentBatches: [],
      hoveredBar: -1,

      // Barcode list
      barcodes: [],
      barcodePage: 1,
      barcodeLastPage: 1,
      barcodeTotal: 0,
      barcodePerPage: 50,
      filters: {
        q: '',
        status: 'all',
        prefix: 'all',
        batch_id: '',
        from: '',
        to: ''
      },

      // Batch list
      batches: [],
      batchPage: 1,
      batchLastPage: 1,
      batchTotal: 0,
      batchPerPage: 25,
      batchSearch: '',
      batchOptions: [],

      // Upload
      pendingFiles: [],
      isDragging: false,
      uploadResults: [],
      uploadForm: {
        courier: 'India Post',
        label: '',
        notes: ''
      },

      // Dialogs
      showVoidConfirm: false,
      showDeleteConfirm: false,
      voidTarget: null,
      deleteTarget: null,

      chart: CHART,
      barcodeColumns: [
        { key: 'code', label: 'Barcode', width: '190px' },
        { key: 'prefix', label: 'Series', width: '80px' },
        { key: 'status', label: 'Status', width: '120px' },
        { key: 'order', label: 'Order', width: '150px' },
        { key: 'used_on', label: 'Used On', width: '120px' },
        { key: 'batch', label: 'Batch' },
        { key: 'note', label: 'Note' },
        { key: 'actions', label: '', align: 'right', width: '120px' }
      ],
      batchColumns: [
        { key: 'name', label: 'Batch' },
        { key: 'range', label: 'Code Range', width: '270px' },
        { key: 'total_count', label: 'Codes', align: 'right', width: '90px' },
        { key: 'used_count', label: 'Used', align: 'right', width: '90px' },
        { key: 'available_count', label: 'Available', align: 'right', width: '100px' },
        { key: 'utilisation', label: 'Utilisation', width: '150px' },
        { key: 'uploaded', label: 'Uploaded', width: '170px' },
        { key: 'actions', label: '', align: 'right', width: '230px' }
      ]
    }
  },
  computed: {
    visibleTabs () {
      const tabs = [
        { key: 'overview', label: 'Overview' },
        { key: 'barcodes', label: 'All Barcodes' },
        { key: 'batches', label: 'Batches' }
      ]
      if (this.canManage) tabs.push({ key: 'upload', label: 'Upload' })
      return tabs
    },
    canManage () {
      const roles = getStoredRoles().map((role) => String(role).toLowerCase())
      return roles.some((role) => MANAGE_ROLES.includes(role))
    },
    courierOptions () {
      return [
        { value: 'India Post', label: 'India Post' },
        { value: 'DTDC', label: 'DTDC' },
        { value: 'Delhivery', label: 'Delhivery' },
        { value: 'Professional', label: 'Professional' },
        { value: 'Other', label: 'Other' }
      ]
    },
    statusFilterOptions () {
      return [
        { value: 'all', label: 'Any status' },
        { value: 'available', label: 'Available' },
        { value: 'used', label: 'Used' },
        { value: 'void', label: 'Voided' }
      ]
    },
    prefixFilterOptions () {
      return [
        { value: 'all', label: 'Any series' },
        ...this.series.map((row) => ({ value: row.prefix, label: `${row.prefix} (${row.total})` }))
      ]
    },
    batchFilterOptions () {
      return [
        { value: '', label: 'Any batch' },
        ...this.batchOptions.map((batch) => ({
          value: String(batch.id),
          label: batch.label || batch.source_filename
        }))
      ]
    },
    kpiCards () {
      const t = this.totals
      return [
        {
          key: 'total',
          label: 'Barcodes Held',
          value: this.formatNumber(t.total),
          hint: `${this.formatNumber(t.batches)} sheet${t.batches === 1 ? '' : 's'} uploaded`,
          tone: 'brand',
          icon: ICONS.total
        },
        {
          key: 'used',
          label: 'Used as Tracking ID',
          value: this.formatNumber(t.used),
          hint: `${this.formatNumber(t.used_this_month)} this month`,
          tone: 'indigo',
          icon: ICONS.used
        },
        {
          key: 'available',
          label: 'Still Available',
          value: this.formatNumber(t.available),
          hint: `${this.formatNumber(t.added_this_month)} added this month`,
          tone: 'green',
          icon: ICONS.available
        },
        {
          key: 'void',
          label: 'Voided',
          value: this.formatNumber(t.void),
          hint: 'Damaged or lost labels',
          tone: 'red',
          icon: ICONS.void
        },
        {
          key: 'utilisation',
          label: 'Utilisation',
          value: `${t.utilisation}%`,
          hint: 'Of everything ever uploaded',
          tone: 'slate',
          icon: ICONS.batches
        }
      ]
    },
    utilisationSegments () {
      const t = this.totals
      const total = t.total || 0
      const pct = (n) => (total > 0 ? `${(n / total) * 100}%` : '0%')
      return [
        { key: 'used', label: 'Used', value: t.used, width: pct(t.used) },
        { key: 'available', label: 'Available', value: t.available, width: pct(t.available) },
        { key: 'void', label: 'Voided', value: t.void, width: pct(t.void) }
      ].filter((seg) => seg.value > 0 || total === 0)
    },
    hasTrendData () {
      return this.trend.some((point) => point.used > 0)
    },
    trendMax () {
      const peak = Math.max(1, ...this.trend.map((point) => point.used))
      // Round up to a readable ceiling so the y-axis ticks land on round
      // numbers rather than on the peak itself.
      const magnitude = Math.pow(10, Math.floor(Math.log10(peak)))
      return Math.ceil(peak / magnitude) * magnitude
    },
    chartTicks () {
      const max = this.trendMax
      return [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
        value: fraction,
        label: this.formatCompact(Math.round(max * fraction)),
        y: CHART.padTop + CHART.plotH * (1 - fraction)
      }))
    },
    trendBars () {
      const max = this.trendMax
      const bandW = CHART.plotW / Math.max(1, this.trend.length)
      const barW = Math.min(30, bandW * 0.52)

      return this.trend.map((point, index) => {
        const bandX = CHART.padLeft + bandW * index
        const h = max > 0 ? (point.used / max) * CHART.plotH : 0
        const x = bandX + (bandW - barW) / 2
        const y = CHART.padTop + CHART.plotH - h
        return {
          index,
          month: point.month,
          label: point.label,
          year: point.year,
          used: point.used,
          bandX,
          bandW,
          path: this.roundedBarPath(x, y, barW, h)
        }
      })
    },
    hoveredTrend () {
      const bar = this.trendBars[this.hoveredBar]
      if (!bar) return null
      return {
        ...bar,
        tipLeft: `${((bar.bandX + bar.bandW / 2) / CHART.width) * 100}%`
      }
    },
    barcodeRows () {
      return this.barcodes.map((row) => ({
        id: row.id,
        code: row.code,
        prefix: row.prefix || '—',
        status: row.status,
        status_label: this.statusLabel(row.status),
        status_class: this.statusClass(row.status),
        order: row.used_order_number || '',
        used_on: row.used_at ? this.formatDate(row.used_at) : '—',
        batch: (row.batch && (row.batch.label || row.batch.source_filename)) || '—',
        note: row.note || '—'
      }))
    },
    batchRows () {
      return this.batches.map((batch) => ({
        id: batch.id,
        name: batch.label || batch.source_filename,
        source_filename: batch.label ? batch.source_filename : '',
        range: batch.first_code ? `${batch.first_code} → ${batch.last_code}` : '—',
        total_count: batch.total_count,
        used_count: batch.used_count,
        available_count: batch.available_count,
        utilisation: batch.utilisation,
        uploaded: `${this.formatDate(batch.created_at)}${batch.uploader_name ? ' · ' + batch.uploader_name : ''}`,
        has_file: batch.has_file,
        raw: batch
      }))
    },
    barcodeRangeLabel () {
      return this.rangeLabel(this.barcodePage, this.barcodePerPage, this.barcodes.length, this.barcodeTotal)
    },
    batchRangeLabel () {
      return this.rangeLabel(this.batchPage, this.batchPerPage, this.batches.length, this.batchTotal)
    },
    uploadButtonLabel () {
      if (this.isUploading) return 'Reading PDFs…'
      const n = this.pendingFiles.length
      if (!n) return 'Upload'
      return `Upload ${n} file${n === 1 ? '' : 's'}`
    },
    lastSyncLabel () {
      if (!this.lastSyncedAt) return 'on the last hourly sweep'
      return `at ${this.lastSyncedAt}`
    },
    usedDateRange: {
      get () {
        return { fromDate: this.filters.from, toDate: this.filters.to }
      },
      set (value) {
        this.filters.from = (value && value.fromDate) || ''
        this.filters.to = (value && value.toDate) || ''
      }
    },
    voidConfirmMessage () {
      if (!this.voidTarget) return ''
      return `${this.voidTarget.code} will be taken out of the available pool. It stays on record and can be restored later.`
    },
    deleteConfirmMessage () {
      if (!this.deleteTarget) return ''
      const name = this.deleteTarget.label || this.deleteTarget.source_filename
      return `"${name}" and its ${this.formatNumber(this.deleteTarget.total_count)} barcode(s) will be removed. Batches with used codes cannot be deleted.`
    }
  },
  created () {
    this.loadSummary()
    this.loadBatchOptions()
  },
  methods: {
    // ── Formatting ──────────────────────────────────────────────────
    formatNumber (value) {
      return Number(value || 0).toLocaleString('en-IN')
    },
    formatCompact (value) {
      const n = Number(value || 0)
      if (n >= 1000) return `${Math.round(n / 100) / 10}k`
      return String(n)
    },
    formatDate (value) {
      if (!value) return '—'
      const ms = Date.parse(value)
      if (!Number.isFinite(ms)) return '—'
      const d = new Date(ms)
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`
    },
    formatBytes (bytes) {
      const kb = Number(bytes || 0) / 1024
      if (kb < 1024) return `${Math.round(kb)} KB`
      return `${(kb / 1024).toFixed(1)} MB`
    },
    statusLabel (status) {
      if (status === 'used') return 'Used'
      if (status === 'void') return 'Voided'
      return 'Available'
    },
    statusClass (status) {
      if (status === 'used') return 'bc-pill--used'
      if (status === 'void') return 'bc-pill--void'
      return 'bc-pill--available'
    },
    percentOf (value, total) {
      if (!total) return '0%'
      return `${(Number(value) / Number(total)) * 100}%`
    },
    rangeLabel (page, perPage, count, total) {
      if (!count) return '0'
      const start = (page - 1) * perPage + 1
      return `${this.formatNumber(start)}–${this.formatNumber(start + count - 1)} of ${this.formatNumber(total)}`
    },
    /** Bar with rounded top corners, square-anchored to the baseline. */
    roundedBarPath (x, y, w, h) {
      if (h <= 0) return ''
      const r = Math.min(4, w / 2, h)
      return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
    },

    // ── Requests ────────────────────────────────────────────────────
    apiError (error, fallback) {
      return (error && error.response && error.response.data && error.response.data.message) || fallback
    },
    switchTab (key) {
      if (this.activeTab === key) return
      this.activeTab = key
      this.errorMessage = ''
      this.reloadActiveTab()
    },
    reloadActiveTab () {
      if (this.activeTab === 'overview') return this.loadSummary()
      if (this.activeTab === 'barcodes') return this.loadBarcodes(this.barcodePage)
      if (this.activeTab === 'batches') return this.loadBatches(this.batchPage)
      return Promise.resolve()
    },
    loadSummary () {
      this.isLoading = true
      this.errorMessage = ''
      return axios.get(`${this.$apiAdress}/api/barcodes/summary`)
        .then((response) => {
          const data = response.data || {}
          this.totals = { ...blankTotals(), ...(data.totals || {}) }
          this.series = Array.isArray(data.series) ? data.series : []
          this.trend = Array.isArray(data.trend) ? data.trend : []
          this.recentBatches = Array.isArray(data.recent_batches) ? data.recent_batches : []
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'Unable to load the barcode dashboard.')
        })
        .finally(() => { this.isLoading = false })
    },
    loadBarcodes (page = 1) {
      this.isLoading = true
      this.errorMessage = ''
      return axios.get(`${this.$apiAdress}/api/barcodes`, {
        params: {
          page,
          per_page: this.barcodePerPage,
          q: this.filters.q || undefined,
          status: this.filters.status,
          prefix: this.filters.prefix,
          batch_id: this.filters.batch_id || undefined,
          from: this.filters.from || undefined,
          to: this.filters.to || undefined
        }
      })
        .then((response) => {
          const data = response.data || {}
          this.barcodes = Array.isArray(data.data) ? data.data : []
          this.barcodePage = Number(data.current_page || 1)
          this.barcodeLastPage = Number(data.last_page || 1)
          this.barcodeTotal = Number(data.total || 0)
          this.barcodePerPage = Number(data.per_page || this.barcodePerPage)
        })
        .catch((error) => {
          this.barcodes = []
          this.errorMessage = this.apiError(error, 'Unable to load barcodes.')
        })
        .finally(() => { this.isLoading = false })
    },
    loadBatches (page = 1) {
      this.isLoading = true
      this.errorMessage = ''
      return axios.get(`${this.$apiAdress}/api/barcodes/batches`, {
        params: {
          page,
          per_page: this.batchPerPage,
          q: this.batchSearch || undefined
        }
      })
        .then((response) => {
          const data = response.data || {}
          this.batches = Array.isArray(data.data) ? data.data : []
          this.batchPage = Number(data.current_page || 1)
          this.batchLastPage = Number(data.last_page || 1)
          this.batchTotal = Number(data.total || 0)
          this.batchPerPage = Number(data.per_page || this.batchPerPage)
        })
        .catch((error) => {
          this.batches = []
          this.errorMessage = this.apiError(error, 'Unable to load batches.')
        })
        .finally(() => { this.isLoading = false })
    },
    /** Flat batch list that feeds the "Batch" filter dropdown. */
    loadBatchOptions () {
      return axios.get(`${this.$apiAdress}/api/barcodes/batches`, {
        params: { per_page: 200 }
      })
        .then((response) => {
          const data = response.data || {}
          this.batchOptions = Array.isArray(data.data) ? data.data : []
        })
        .catch(() => { this.batchOptions = [] })
    },
    applyBarcodeFilters () {
      this.barcodePage = 1
      this.loadBarcodes(1)
    },
    resetBarcodeFilters () {
      this.filters = { q: '', status: 'all', prefix: 'all', batch_id: '', from: '', to: '' }
      this.applyBarcodeFilters()
    },
    goToBarcodePage (page) {
      this.loadBarcodes(page)
    },
    goToBatchPage (page) {
      this.loadBatches(page)
    },
    drillIntoSeries (prefix) {
      this.filters = { ...this.filters, prefix, batch_id: '', q: '' }
      this.activeTab = 'barcodes'
      this.loadBarcodes(1)
    },
    drillIntoBatch (batch) {
      if (!batch) return
      this.filters = { q: '', status: 'all', prefix: 'all', batch_id: String(batch.id), from: '', to: '' }
      this.activeTab = 'barcodes'
      this.loadBarcodes(1)
    },
    syncUsage () {
      this.isSyncing = true
      this.errorMessage = ''
      return axios.post(`${this.$apiAdress}/api/barcodes/sync`)
        .then((response) => {
          const data = response.data || {}
          const marked = Number(data.marked || 0)
          const released = Number(data.released || 0)
          this.lastSyncedAt = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
          this.successMessage = marked || released
            ? `Sync complete — ${marked} newly used, ${released} released back to available.`
            : 'Sync complete — everything was already up to date.'
          return this.reloadActiveTab()
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'Sync failed.')
        })
        .finally(() => { this.isSyncing = false })
    },
    exportBarcodes () {
      const params = new URLSearchParams({
        status: this.filters.status,
        prefix: this.filters.prefix
      })
      if (this.filters.q) params.append('q', this.filters.q)
      if (this.filters.batch_id) params.append('batch_id', this.filters.batch_id)
      if (this.filters.from) params.append('from', this.filters.from)
      if (this.filters.to) params.append('to', this.filters.to)

      this.isLoading = true
      axios.get(`${this.$apiAdress}/api/barcodes/export?${params.toString()}`, { responseType: 'blob' })
        .then((response) => {
          this.triggerDownload(response.data, `barcodes_${Date.now()}.csv`)
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'Export failed.')
        })
        .finally(() => { this.isLoading = false })
    },
    downloadBatchPdf (batch) {
      // The route sits behind auth:sanctum, so a bare window.open would
      // arrive without the bearer token — fetch through axios instead.
      axios.get(`${this.$apiAdress}/api/barcodes/batches/${batch.id}/file`, { responseType: 'blob' })
        .then((response) => {
          this.triggerDownload(response.data, batch.source_filename || `batch-${batch.id}.pdf`)
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'The original PDF could not be downloaded.')
        })
    },
    triggerDownload (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    // ── Void / restore / delete ─────────────────────────────────────
    askVoid (row) {
      this.voidTarget = row
      this.showVoidConfirm = true
    },
    confirmVoid () {
      if (!this.voidTarget) return
      this.setBarcodeStatus(this.voidTarget.id, 'void', `${this.voidTarget.code} voided.`)
        .finally(() => { this.showVoidConfirm = false; this.voidTarget = null })
    },
    restoreBarcode (row) {
      this.setBarcodeStatus(row.id, 'available', `${row.code} is available again.`)
    },
    setBarcodeStatus (id, status, message) {
      this.isMutating = true
      this.errorMessage = ''
      return axios.patch(`${this.$apiAdress}/api/barcodes/${id}`, { status })
        .then(() => {
          this.successMessage = message
          return Promise.all([this.loadBarcodes(this.barcodePage), this.loadSummary()])
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'Could not update this barcode.')
        })
        .finally(() => { this.isMutating = false })
    },
    askDeleteBatch (batch) {
      this.deleteTarget = batch
      this.showDeleteConfirm = true
    },
    confirmDeleteBatch () {
      if (!this.deleteTarget) return
      this.isMutating = true
      this.errorMessage = ''
      axios.delete(`${this.$apiAdress}/api/barcodes/batches/${this.deleteTarget.id}`)
        .then(() => {
          this.successMessage = 'Batch deleted.'
          this.showDeleteConfirm = false
          this.deleteTarget = null
          return Promise.all([this.loadBatches(this.batchPage), this.loadSummary(), this.loadBatchOptions()])
        })
        .catch((error) => {
          this.errorMessage = this.apiError(error, 'Could not delete this batch.')
          this.showDeleteConfirm = false
        })
        .finally(() => { this.isMutating = false })
    },

    // ── Upload ──────────────────────────────────────────────────────
    onDrop (event) {
      this.isDragging = false
      this.addFiles(Array.from((event.dataTransfer && event.dataTransfer.files) || []))
    },
    onFilePick (event) {
      this.addFiles(Array.from(event.target.files || []))
      event.target.value = ''
    },
    addFiles (files) {
      const pdfs = files.filter((file) => /\.pdf$/i.test(file.name))
      const rejected = files.length - pdfs.length
      if (rejected > 0) {
        this.errorMessage = `${rejected} file(s) skipped — only PDFs can be read.`
      }
      // Dedupe by name+size so a double-drop doesn't queue the same sheet twice.
      const seen = new Set(this.pendingFiles.map((file) => `${file.name}:${file.size}`))
      pdfs.forEach((file) => {
        const key = `${file.name}:${file.size}`
        if (!seen.has(key)) {
          seen.add(key)
          this.pendingFiles.push(file)
        }
      })
    },
    removeFile (index) {
      this.pendingFiles.splice(index, 1)
    },
    submitUpload () {
      if (!this.pendingFiles.length) return

      const form = new FormData()
      this.pendingFiles.forEach((file) => form.append('files[]', file))
      if (this.uploadForm.courier) form.append('courier', this.uploadForm.courier)
      if (this.uploadForm.label) form.append('label', this.uploadForm.label)
      if (this.uploadForm.notes) form.append('notes', this.uploadForm.notes)

      this.isUploading = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/barcodes/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then((response) => {
          const data = response.data || {}
          this.uploadResults = Array.isArray(data.results) ? data.results : []
          const inserted = Number(data.inserted || 0)
          const duplicates = Number(data.duplicates || 0)
          this.successMessage = inserted
            ? `Stored ${this.formatNumber(inserted)} new barcode(s)${duplicates ? `, skipped ${this.formatNumber(duplicates)} already on record` : ''}.`
            : 'No new barcodes were added — every code in those PDFs was already on record.'
          this.pendingFiles = []
          this.uploadForm.label = ''
          this.uploadForm.notes = ''
          return Promise.all([this.loadSummary(), this.loadBatchOptions()])
        })
        .catch((error) => {
          const payload = error && error.response && error.response.data
          if (payload && payload.error) {
            this.errorMessage = Object.values(payload.error).flat().join(' ')
          } else {
            this.errorMessage = this.apiError(error, 'Upload failed.')
          }
        })
        .finally(() => { this.isUploading = false })
    }
  }
}
</script>

<style scoped>
.barcodes-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.bc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.bc-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.bc-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.bc-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.bc-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.bc-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.bc-toolbar__actions >>> .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.bc-toolbar__actions >>> .btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.bc-toolbar__actions >>> .btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.bc-toolbar__actions >>> .btn:focus {
  outline: none;
}

.bc-toolbar__actions >>> .btn:focus-visible {
  outline: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.refresh-btn__icon,
.bc-sync-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.bc-toolbar__actions >>> .btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.bc-toolbar__actions >>> .btn.is-loading .refresh-btn__icon,
.bc-toolbar__actions >>> .btn.is-loading .bc-sync-btn__icon {
  animation: bc-spin 0.9s linear infinite;
}

@keyframes bc-spin {
  to { transform: rotate(360deg); }
}

.bc-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Tabs ── */
.bc-tab-bar {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  width: fit-content;
  max-width: 100%;
  padding: 0.55rem 0.3rem;
  margin: 0 0 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.bc-tab-bar::-webkit-scrollbar { display: none; }

.bc-tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
}

.bc-tab:focus { outline: none; box-shadow: none; }
.bc-tab:focus-visible { box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
.bc-tab:hover:not(.is-active) { background: #f1f5f9; color: #334155; }

.bc-tab.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
}

/* ── KPI row ── */
.bc-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.bc-kpi {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.bc-kpi__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  flex-shrink: 0;
}

.bc-kpi__chip--brand  { background: #e8f6f1; color: #0e5b46; }
.bc-kpi__chip--indigo { background: #eef2ff; color: #4338ca; }
.bc-kpi__chip--green  { background: #ecfdf5; color: #047857; }
.bc-kpi__chip--red    { background: #fef2f2; color: #b91c1c; }
.bc-kpi__chip--slate  { background: #f1f5f9; color: #475569; }

.bc-kpi__body { min-width: 0; }

.bc-kpi__label {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.bc-kpi__value {
  margin: 0.15rem 0 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2a44;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.bc-kpi__value.is-brand { color: #0e5b46; }

.bc-kpi__hint {
  margin: 0.15rem 0 0;
  font-size: 0.74rem;
  color: #94a3b8;
}

/* ── Section cards ── */
.bc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.bc-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.bc-grid .bc-section { margin-bottom: 0; }

.bc-section--chart { grid-column: span 2; }

@media (max-width: 1100px) {
  .bc-section--chart { grid-column: span 1; }
}

.bc-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.bc-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.bc-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.bc-section__body { padding: 1rem; }

.bc-empty {
  padding: 1.4rem 0.5rem;
  text-align: center;
  font-size: 0.83rem;
  color: #94a3b8;
}

.bc-link-btn {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  cursor: pointer;
}

.bc-link-btn:focus { outline: none; }
.bc-link-btn:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-link-btn:hover { text-decoration: underline; }

/* ── Utilisation meter ── */
.bc-meter-figure {
  margin: 0 0 0.75rem;
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.bc-meter-figure__value {
  font-size: 2rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.03em;
  line-height: 1;
}

.bc-meter-figure__unit {
  font-size: 0.8rem;
  color: #6b7280;
}

.bc-meter {
  display: flex;
  gap: 2px;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: #f1f5f9;
}

.bc-meter__seg { display: block; height: 100%; }
.bc-meter__seg--used      { background: #16a07a; }
.bc-meter__seg--available { background: #cbd5e1; }
.bc-meter__seg--void      { background: #f87171; }
.bc-meter__empty { flex: 1; }

.bc-legend {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.1rem;
}

.bc-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #64748b;
}

.bc-legend__swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.bc-legend__swatch--used      { background: #16a07a; }
.bc-legend__swatch--available { background: #cbd5e1; }
.bc-legend__swatch--void      { background: #f87171; }

.bc-legend__value {
  font-weight: 700;
  color: #1f2a44;
}

.bc-meter-note {
  margin: 0.9rem 0 0;
  font-size: 0.74rem;
  line-height: 1.5;
  color: #94a3b8;
}

/* ── Trend chart ── */
.bc-chart {
  position: relative;
  width: 100%;
}

.bc-chart__svg {
  width: 100%;
  height: auto;
  display: block;
  overflow: visible;
}

.bc-chart__grid {
  stroke: #eef1f7;
  stroke-width: 1;
}

.bc-chart__axis {
  fill: #94a3b8;
  font-size: 10px;
}

.bc-chart__bar {
  fill: #16a07a;
  transition: fill 0.15s ease;
}

.bc-chart__bar.is-hovered { fill: #0e5b46; }

.bc-chart__hit {
  fill: transparent;
  cursor: pointer;
}

.bc-chart__tip {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  padding: 0.4rem 0.6rem;
  background: #1f2a44;
  color: #ffffff;
  border-radius: 8px;
  font-size: 0.74rem;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.22);
}

.bc-chart__tip-head { opacity: 0.75; }
.bc-chart__tip-value { font-weight: 700; }

/* ── Series breakdown ── */
.bc-series {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.bc-series__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
}

.bc-series__name {
  border: none;
  background: transparent;
  padding: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
  cursor: pointer;
}

.bc-series__name:hover { color: #0e5b46; text-decoration: underline; }
.bc-series__name:focus { outline: none; }
.bc-series__name:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-series__counts {
  font-size: 0.76rem;
  color: #64748b;
}

.bc-series__counts strong { color: #1f2a44; }

.bc-series__track {
  height: 8px;
  border-radius: 4px;
  background: #f1f5f9;
  overflow: hidden;
}

.bc-series__fill {
  display: block;
  height: 100%;
  border-radius: 4px;
  background: #16a07a;
}

/* ── Recent uploads ── */
.bc-recent {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.bc-recent__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
}

.bc-recent__row:last-child { border-bottom: none; }

.bc-recent__main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.bc-recent__name {
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  cursor: pointer;
}

.bc-recent__name:hover { color: #0e5b46; text-decoration: underline; }
.bc-recent__name:focus { outline: none; }
.bc-recent__name:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-recent__range {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  color: #94a3b8;
}

.bc-recent__meta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.bc-recent__date {
  font-size: 0.74rem;
  color: #94a3b8;
}

.bc-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
}

.bc-chip--used { background: #e8f6f1; color: #0e5b46; }

/* ── Filters ── */
.bc-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.bc-filters--upload { margin-top: 1rem; }

.bc-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 200px;
  min-width: 0;
}

.bc-filter--wide   { flex: 1 1 280px; }
.bc-filter--range  { flex: 1 1 300px; }
.bc-filter--full   { flex: 1 1 100%; }
.bc-filter--action { flex: 0 0 auto; }

.bc-filter-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.bc-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.bc-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #2f3640;
  background: #ffffff;
}

.bc-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

/* ── Buttons ── */
.bc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 84px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.bc-btn--lg { height: 40px; padding: 0 1.4rem; }

.bc-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.bc-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
}

.bc-btn--ghost {
  background: #ffffff;
  border-color: #e5e9f2;
  color: #1f2a44;
}

.bc-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.bc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.bc-btn:focus { outline: none; }
.bc-btn::-moz-focus-inner { border: 0; }

.bc-btn--primary:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-btn--ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* ── Table cells ── */
.bc-table-hint {
  font-size: 0.76rem;
  color: #6b7280;
}

.bc-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.bc-code--sm { font-size: 0.75rem; font-weight: 500; color: #64748b; }

.bc-order {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  color: #4338ca;
}

.bc-muted { color: #cbd5e1; }

.bc-subtle {
  display: block;
  font-size: 0.7rem;
  color: #94a3b8;
}

.bc-linkish {
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2a44;
  cursor: pointer;
}

.bc-linkish:hover { color: #0e5b46; text-decoration: underline; }
.bc-linkish:focus { outline: none; }
.bc-linkish:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.bc-pill--available { background: #ecfdf5; color: #047857; }
.bc-pill--used      { background: #eef2ff; color: #4338ca; }
.bc-pill--void      { background: #fef2f2; color: #b91c1c; }

.bc-mini-meter {
  display: inline-block;
  width: 90px;
  height: 7px;
  border-radius: 4px;
  background: #f1f5f9;
  overflow: hidden;
  vertical-align: middle;
}

.bc-mini-meter__fill {
  display: block;
  height: 100%;
  background: #16a07a;
}

.bc-mini-meter__label {
  margin-left: 0.45rem;
  font-size: 0.74rem;
  font-weight: 600;
  color: #64748b;
  vertical-align: middle;
}

/* ── Row actions (SettingsProducts pattern) ── */
.bc-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  justify-content: flex-end;
}

.bc-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.bc-action-btn:focus { outline: none; }
.bc-action-btn::-moz-focus-inner { border: 0; }
.bc-action-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-action-btn--view:hover,
.bc-action-btn--restore:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.bc-action-btn--void:hover,
.bc-action-btn--delete:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.bc-action-btn--void:focus-visible,
.bc-action-btn--delete:focus-visible {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}

/* ── Pagination ── */
.bc-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}

.bc-pagination-info {
  font-size: 0.78rem;
  color: #6b7280;
}

/* ── Upload ── */
.bc-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  border: 2px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
  text-align: center;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.bc-drop.is-active {
  border-color: #16a07a;
  background: #f5fbf8;
}

.bc-drop__icon { color: #16a07a; }

.bc-drop__title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
}

.bc-drop__hint {
  margin: 0 0 0.4rem;
  max-width: 460px;
  font-size: 0.78rem;
  line-height: 1.5;
  color: #6b7280;
}

.bc-drop__input { display: none; }

.bc-filelist {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.bc-filelist__row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.65rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
}

.bc-filelist__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  color: #1f2a44;
}

.bc-filelist__size {
  font-size: 0.74rem;
  color: #94a3b8;
}

.bc-filelist__remove {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 6px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
}

.bc-filelist__remove:hover { background: #fef2f2; color: #b91c1c; }
.bc-filelist__remove:focus { outline: none; }
.bc-filelist__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bc-upload-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.bc-results {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.bc-result {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  font-size: 0.8rem;
}

.bc-result.is-ok  { background: #f5fbf8; border-color: #d1f0e4; }
.bc-result.is-bad { background: #fef2f2; border-color: #fecaca; }

.bc-result__file {
  font-weight: 700;
  color: #1f2a44;
}

.bc-result__detail { color: #4b5563; }
.bc-result.is-bad .bc-result__detail { color: #b91c1c; }
</style>
